import { pxToEm } from 'util/pxToEm';
import { Box, Typography, useTheme, Checkbox, CircularProgress } from '@mui/material';
import { AddIcon } from 'components/icons';
import { Button, ConfirmationModal, Modal, TextInput } from 'components/ui';
import { colorBorderLight } from 'customColors';
import React, { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useSchoolsApi } from 'services/api';
import { useSnackbar } from 'notistack';

interface MajorMapping {
  id: string;
  majorId: string;
  majorName: string;
}

interface AvailableMajor {
  id: string;
  name: string;
}

export interface MajorsTabProps {
  schoolId: string;
}

export const MajorsTab = ({ schoolId }: MajorsTabProps): ReactElement => {
  const theme = useTheme();
  const schoolsApi = useSchoolsApi();
  const { enqueueSnackbar } = useSnackbar();

  const [mappings, setMappings] = useState<MajorMapping[]>([]);
  const [availableMajors, setAvailableMajors] = useState<AvailableMajor[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const [showAddModal, setShowAddModal] = useState(false);
  const [modalSearch, setModalSearch] = useState('');
  const [selectedMajorIds, setSelectedMajorIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);

  const [selectedMappingIds, setSelectedMappingIds] = useState<Set<string>>(new Set());
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchMappings = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await schoolsApi.getSchoolMajorMappings(schoolId);
      if (result.success)
        setMappings(result.value.mappings);

    } catch {
      enqueueSnackbar('Error fetching majors', { variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  }, [schoolId, schoolsApi]);

  const fetchAvailable = useCallback(async () => {
    try {
      const result = await schoolsApi.getAvailableMajors(schoolId);
      if (result.success)
        setAvailableMajors(result.value.majors);

    } catch {
      enqueueSnackbar('Error fetching available majors', { variant: 'error' });
    }
  }, [schoolId, schoolsApi]);

  useEffect(() => {
    void fetchMappings();
    void fetchAvailable();
  }, [fetchMappings, fetchAvailable]);

  const filteredMappings = useMemo(() => {
    if (!searchTerm.trim()) return mappings;
    const term = searchTerm.toLowerCase();
    return mappings.filter(m => m.majorName.toLowerCase().includes(term));
  }, [mappings, searchTerm]);

  useEffect(() => {
    setSelectedMappingIds(prev => {
      if (prev.size === 0) return prev;
      const validIds = new Set(mappings.map(m => m.id));
      let changed = false;
      const next = new Set<string>();
      prev.forEach(id => {
        if (validIds.has(id)) next.add(id);
        else changed = true;
      });
      return changed ? next : prev;
    });
  }, [mappings]);

  const filteredAvailable = useMemo(() => {
    if (!modalSearch.trim()) return availableMajors;
    const term = modalSearch.toLowerCase();
    return availableMajors.filter(m => m.name.toLowerCase().includes(term));
  }, [availableMajors, modalSearch]);

  const toggleMajorSelection = (majorId: string) => {
    setSelectedMajorIds(prev => {
      const next = new Set(prev);
      if (next.has(majorId)) next.delete(majorId);
      else next.add(majorId);
      return next;
    });
  };

  const toggleMappingSelection = (mappingId: string) => {
    setSelectedMappingIds(prev => {
      const next = new Set(prev);
      if (next.has(mappingId)) next.delete(mappingId);
      else next.add(mappingId);
      return next;
    });
  };

  const filteredSelectedCount = useMemo(
    () => filteredMappings.filter(m => selectedMappingIds.has(m.id)).length,
    [filteredMappings, selectedMappingIds],
  );

  const allFilteredSelected =
    filteredMappings.length > 0 &&
    filteredSelectedCount === filteredMappings.length;
  const someFilteredSelected =
    filteredSelectedCount > 0 && !allFilteredSelected;

  const toggleSelectAllFiltered = () => {
    setSelectedMappingIds(prev => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        filteredMappings.forEach(m => next.delete(m.id));
      } else {
        filteredMappings.forEach(m => next.add(m.id));
      }
      return next;
    });
  };

  const handleOpenAddModal = () => {
    setModalSearch('');
    setSelectedMajorIds(new Set());
    setShowAddModal(true);
  };

  const handleAddSelected = async () => {
    if (selectedMajorIds.size === 0) return;
    setIsAdding(true);
    try {
      let successCount = 0;
      for (const majorId of selectedMajorIds) {
        const result = await schoolsApi.createSchoolMajorMapping(schoolId, majorId);
        if (result.success) successCount++;
      }
      if (successCount > 0) {
        enqueueSnackbar(
          `${successCount} major${successCount !== 1 ? 's' : ''} added`,
          { variant: 'success' },
        );
      }
      setShowAddModal(false);
      await fetchMappings();
      await fetchAvailable();
    } catch {
      enqueueSnackbar('Error adding majors', { variant: 'error' });
    } finally {
      setIsAdding(false);
    }
  };

  const handleOpenDeleteConfirmation = () => {
    if (selectedMappingIds.size === 0) return;
    setShowDeleteConfirmation(true);
  };

  const confirmBulkDelete = async () => {
    if (selectedMappingIds.size === 0) return;
    setIsDeleting(true);
    try {
      const result = await schoolsApi.bulkDeleteSchoolMajorMappings(
        schoolId,
        Array.from(selectedMappingIds),
      );
      if (result.success) {
        enqueueSnackbar(
          `${result.value.deleted} major${result.value.deleted !== 1 ? 's' : ''} removed`,
          { variant: 'success' },
        );
        setSelectedMappingIds(new Set());
        await fetchMappings();
        await fetchAvailable();
      } else {
        throw new Error(result.error.message);
      }
    } catch {
      enqueueSnackbar('Error removing majors', { variant: 'error' });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirmation(false);
    }
  };

  const selectedCount = selectedMappingIds.size;

  return (
    <Box maxWidth={880}>
      <Box
        mt={30}
        display="flex"
        justifyContent={{ xs: 'space-between', md: 'flex-start' }}
        alignItems="center"
        gap={32}
      >
        <Typography sx={{ fontWeight: 600, fontSize: '1.25em' }}>
          Majors
        </Typography>
        <Box
          display="flex"
          alignItems="center"
          gap={8}
          style={{ cursor: 'pointer' }}
          onClick={handleOpenAddModal}
        >
          <AddIcon />
          Add Majors
        </Box>
      </Box>

      <Box mt={24} maxWidth={300}>
        <TextInput
          label="Search Majors"
          placeholder="Filter by name..."
          onChange={e => setSearchTerm(e.target.value)}
          value={searchTerm}
        />
      </Box>

      {isLoading ? (
        <Box mt={30} display="flex" justifyContent="center">
          <CircularProgress size={24} />
        </Box>
      ) : mappings.length === 0 ? (
        <Box mt={30}>
          <Typography sx={{ fontWeight: 400, fontSize: '1rem' }}>
            No majors mapped to this school.
          </Typography>
        </Box>
      ) : (
        <Box mt={24}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={16}
            mb={8}
            sx={{ flexWrap: 'wrap' }}
          >
            <Box display="flex" alignItems="center" gap={4}>
              <Checkbox
                size="small"
                checked={allFilteredSelected}
                indeterminate={someFilteredSelected}
                disabled={filteredMappings.length === 0}
                onChange={toggleSelectAllFiltered}
                sx={{ p: '6px' }}
              />
              <Typography sx={{ fontSize: '0.875em', fontWeight: 600 }}>
                {mappings.length} major{mappings.length !== 1 ? 's' : ''} mapped
                {selectedCount > 0 && ` (${selectedCount} selected)`}
              </Typography>
            </Box>
            {selectedCount > 0 && (
              <Button
                minWidth={140}
                variant="vibrant"
                disabled={isDeleting}
                onClick={handleOpenDeleteConfirmation}
                sx={{
                  backgroundColor: theme.palette.error.main,
                  color: theme.palette.common.white,
                  borderColor: theme.palette.error.main,
                  '&:hover': {
                    backgroundColor: theme.palette.error.dark,
                    color: theme.palette.common.white,
                  },
                }}
              >
                {isDeleting
                  ? 'Removing...'
                  : `Remove Selected (${selectedCount})`}
              </Button>
            )}
          </Box>

          {filteredMappings.length === 0 ? (
            <Typography sx={{ fontSize: '0.9em', color: 'text.secondary' }}>
              No majors match your search.
            </Typography>
          ) : (
            filteredMappings.map(mapping => (
              <Box
                key={mapping.id}
                display="flex"
                alignItems="center"
                py={4}
                px={4}
                sx={{
                  borderBottom: `1px solid ${colorBorderLight}`,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                }}
                onClick={() => toggleMappingSelection(mapping.id)}
              >
                <Checkbox
                  checked={selectedMappingIds.has(mapping.id)}
                  size="small"
                  sx={{ p: '6px' }}
                />
                <Typography sx={{ fontWeight: 400, fontSize: '0.9em' }}>
                  {mapping.majorName}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      )}

      {showAddModal && (
        <Modal
          title="Add Majors"
          open={showAddModal}
          onClose={() => setShowAddModal(false)}
        >
          <Box sx={{ width: 500, maxWidth: { xs: '100%', md: '80vw' } }}>
            <Box mb={16}>
              <TextInput
                label="Search Available Majors"
                placeholder="Search by name..."
                value={modalSearch}
                onChange={e => setModalSearch(e.target.value)}
              />
            </Box>

            <Box
              sx={{
                maxHeight: 360,
                overflowY: 'auto',
                border: `1px solid ${colorBorderLight}`,
                borderRadius: '8px',
              }}
            >
              {filteredAvailable.length === 0 ? (
                <Box p={16}>
                  <Typography sx={{ fontSize: '0.9em', color: 'text.secondary' }}>
                    {availableMajors.length === 0
                      ? 'All majors are already mapped to this school.'
                      : 'No available majors match your search.'}
                  </Typography>
                </Box>
              ) : (
                filteredAvailable.map(major => (
                  <Box
                    key={major.id}
                    display="flex"
                    alignItems="center"
                    px={8}
                    py={4}
                    sx={{
                      borderBottom: `1px solid ${colorBorderLight}`,
                      cursor: 'pointer',
                      '&:last-child': { borderBottom: 'none' },
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={() => toggleMajorSelection(major.id)}
                  >
                    <Checkbox
                      checked={selectedMajorIds.has(major.id)}
                      size="small"
                      sx={{ p: '6px' }}
                    />
                    <Typography sx={{ fontSize: '0.9em' }}>
                      {major.name}
                    </Typography>
                  </Box>
                ))
              )}
            </Box>

            <Box mt={24} display="flex" gap={16}>
              <Button minWidth={100} inverted onClick={() => setShowAddModal(false)}>
                Cancel
              </Button>
              <Button
                minWidth={100}
                variant="vibrant"
                disabled={selectedMajorIds.size === 0 || isAdding}
                onClick={handleAddSelected}
              >
                {isAdding
                  ? 'Adding...'
                  : `Add Selected (${selectedMajorIds.size})`}
              </Button>
            </Box>
          </Box>
        </Modal>
      )}

      <ConfirmationModal
        title="Confirm Remove"
        open={showDeleteConfirmation}
        onCancel={() => setShowDeleteConfirmation(false)}
        onConfirm={confirmBulkDelete}
        message={`Are you sure you want to remove ${selectedCount} major${selectedCount !== 1 ? 's' : ''} from this school?`}
        body={
          <Typography fontSize={pxToEm(14)} color="text.secondary" mt={16}>
            Students at this school will no longer see {selectedCount === 1 ? 'this major' : 'these majors'} during onboarding.
          </Typography>
        }
        confirmAction={isDeleting ? 'Removing...' : 'Remove'}
        cancelAction="Cancel"
      />
    </Box>
  );
};
